'use client';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  LoadingIndicator,
} from '@commenda/kakashi';
import { useContext, useMemo } from 'react';
import { FormContext } from '../../hooks/FormContext';
import { isRuleActive } from '../../utils/validation';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';
import { useCommendaOSList } from '@commenda/api/commenda-os-dsl';

interface EntitySelectProps {
  fieldId: string;
  label?: string;
  placeholder?: string;
  guidance?: { text?: string };
  description?: string;
  validation?: {
    rules?: {
      required?: { enabled?: boolean } | boolean;
    };
  };
  props?: {
    entity?: string;
    mode?: 'select' | 'select-or-create' | 'select-and-edit' | 'full';
    displayField?: string;
    valueField?: string;
    listPath?: string;
    disabled?: boolean;
  };
}

/**
 * EntitySelect Component
 *
 * A select component that fetches entities from Commenda OS DSL and allows
 * users to select one. This is used for Use Case 1: selecting a corporation.
 *
 * Props:
 * - entity: The entity type to fetch (e.g., 'corporation', 'company')
 * - displayField: The field path to use as the display label (e.g., 'generalCorporationInfo.legalName')
 * - valueField: The field path to use as the stored value (e.g., 'id')
 * - mode: The selection mode (currently only 'select' is implemented)
 */
export const EntitySelectComponent = ({
  fieldId,
  label,
  placeholder,
  guidance,
  description,
  validation,
  props = {},
}: EntitySelectProps) => {
  const {
    formData,
    updateField,
    errors,
    validateField,
    isFieldVisible,
    isFieldDisabled,
    dslContext,
  } = useContext(FormContext);

  const {
    entity = 'corporation',
    displayField = 'generalCorporationInfo.legalName',
    valueField = 'id',
    listPath,
    disabled: propsDisabled = false,
  } = props;

  // Fetch entities from Commenda OS using listPath from schema if available
  const { data: entities, isLoading, error } = useCommendaOSList<
    Record<string, unknown>
  >(entity, dslContext as { companyId: number } | undefined, listPath);


  const value = formData[fieldId] ? String(formData[fieldId]) : '';
  const disabled = propsDisabled || isFieldDisabled(fieldId);
  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  // Transform entities into select options
  const options = useMemo(() => {
    if (!entities || !Array.isArray(entities)) return [];

    return entities.map((entity) => {
      // Get nested value using dot notation path
      const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
        return path.split('.').reduce((current: unknown, key: string) => {
          if (current && typeof current === 'object') {
            return (current as Record<string, unknown>)[key];
          }
          return undefined;
        }, obj);
      };

      const displayValue = getNestedValue(entity, displayField);
      const idValue = getNestedValue(entity, valueField);

      return {
        value: String(idValue ?? ''),
        label: String(displayValue ?? `${entity} ${idValue}`),
      };
    });
  }, [entities, displayField, valueField]);

  const handleChange = (val: string) => {
    updateField(fieldId, val);
    validateField(fieldId, val, validation?.rules);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(label || description) && (
          <LabelWithGuidanceTooltip
            label={label}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={description}
          />
        )}
        <div className="flex items-center justify-center p-4 border rounded-md bg-bg-secondary">
          <LoadingIndicator size={16} />
          <span className="ml-2 text-sm text-text-tertiary">
            Loading {entity}s...
          </span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(label || description) && (
          <LabelWithGuidanceTooltip
            label={label}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={description}
          />
        )}
        <div className="p-4 border border-border-error rounded-md bg-bg-error-subtle">
          <p className="text-sm text-text-error">
            Failed to load {entity}s. Please try again.
          </p>
        </div>
      </div>
    );
  }

  // Show empty state if no context
  if (!dslContext?.companyId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(label || description) && (
          <LabelWithGuidanceTooltip
            label={label}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={description}
          />
        )}
        <div className="p-4 border border-border-warning rounded-md bg-bg-warning-subtle">
          <p className="text-sm text-text-warning">
            Company context required to load {entity}s.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={description}
        />
      )}
      <Select value={value} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger error={errors[fieldId]}>
          <SelectValue
            placeholder={placeholder || `Select a ${entity}...`}
          />
        </SelectTrigger>
        <SelectContent>
          {options.length === 0 ? (
            <div className="p-4 text-center text-sm text-text-tertiary">
              No {entity}s found
            </div>
          ) : (
            options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {errors[fieldId] && (
        <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
      )}
    </div>
  );
};
